import {
	Divider,
	Button,
	Flex,
	HStack,
	Tab,
	TabList,
	TabPanel,
	TabPanels,
	Tabs,
	Text,
	Box,
	useToast,
	Textarea,
	Badge,
} from "@chakra-ui/react";
import IssbContainer from "./IssbContainer";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { H1 } from "components/text/headings";
import reportDocService, { baseIdFromTypedId } from "api/issb/reportdoc.service";
import { useQuery, useQueryClient } from "react-query";
import { useAuth } from "context";
import PageHeader from "components/text/PageHeader";
import WizardStepWrapper from "./WizardStepWrapper";
import ReportDocSourcesPanel from "./ReportDocSourcesPanel";
import RichTextEditor from "./IssbDataInput/components/RichTextEditor/RichTextEditor";
import DevOnly from "context/Auth/DevOnly";
import { WideMarginsWrapper } from "./IssbDataInput/AnswerDetailPage";
import LocalSpinner from "components/LocalSpinner";
import AIProgressWidget from "pages/ISSB/AITasks/AIProgressWidget";
import ReportSectionChecklist from "pages/ISSB/AITasks/ReportSectionChecklist";
import {
	AGENT_CANVAS,
	postReportWriter
} from "api/ai/ai.service";
import _ from "lodash";
import {
	ArrowCircleUp,
	Copy,
	Files,
	Sparkle,
	Chat,
	ThumbsUp,
	ThumbsDown,
	ClockCounterClockwise,
	ShareFat,
	Trash,
} from "@phosphor-icons/react";
import { colors } from "theme/foundations/colors";
import { showSuccessToast } from "utils";
import { ReportDocHeader } from "./ReportDocListPage";
import { routes } from "config/paths";
import { useConfirmation } from "context/Mutation/ConfirmationDialogContext";
import LifecycleState from "api/common/LifecycleState";
import StatusToggle from "pages/ISSB/IssbDataInput/components/StatusToggle";
import { toTitleCase } from "utils/text-utils";
import { Card } from "components/Card";
import { H3 } from "components/text/headings";
import ReportDocEditorPanel, { getFileinfoSpec, ReportDocStateToggle } from "./ReportDocEditorPanel";
import { FaSpinner } from "react-icons/fa";
import PropInput from "components/PropInput";
import { useContextualPrompts } from "../../hooks/query/prompt/useContextualPrompts";

const STEPS = ["SELECT_SOURCES", "FORMAT", "GENERATING", "EDIT_REPORT"];
const EDIT_REPORT_STEP_INDEX = STEPS.indexOf("EDIT_REPORT");

/**
 * https://www.figma.com/design/uDql1qJTNBIvGnFDsOpWV4/ESG360-AIR?node-id=5658-164594&t=VmvkPaqqmcS7OkCY-1
 *
 * @returns
 */
export default function ReportDocEditorPage() {
	const { getAccessTokenSilently } = useAuth();
	const { id: reportDocId } = useParams();
	if (!reportDocId) {
		// this should never happen
		console.error("No report doc id");
	}

	const [stepIndex, setStepIndex] = useState(0);
	const step = STEPS[stepIndex];
	const [_nextDisabled, setNextDisabled] = useState(false);
	let nextDisabled = _nextDisabled;

	const [isProcessing, setIsProcessing] = useState(false);
	const [questionFilter, setQuestionFilter] = useState("*");

	const queryClient = useQueryClient();
	/** move to the next step + AI generate the draft report if that is the next step! */
	const gotoNext = async () => {
		// Save source selections and stage filter to backend when leaving the sources step
		if (step === "SELECT_SOURCES") {
			reportDoc.sourcesAllowed = sourceTypedIds;
			reportDoc.questionFilter = questionFilter;
			reportDocService.update(reportDocId, reportDoc);
		}
		const newStepIndex = stepIndex + 1;
		setStepIndex(newStepIndex);
		const newStep = STEPS[newStepIndex];
		if (newStep === "GENERATING") {
			if (isProcessing) {
				console.warn("(gotoNext) dupe call? Skipping generation because we are already processing");
				return;
			}
			if (format && !reportDoc.title) reportDoc.title = toTitleCase(format); // paranoia
			setIsProcessing(true);
			const axiosConfig = {
				params: {
					byAI: true,
					aiId: "template", // count the initial template as "AI" (since we dont have a marker for "by template")
				},
			};
			await reportDocService.update(reportDocId, reportDoc, axiosConfig); // save the title
			// kick off the generation	  
			const _fileinfoSpec = getFileinfoSpec(reportDoc);
			let message = "Generate a canvas report in the format: " + format;
			// User-visible chat bubble: prefer the prompt description for the format label so the bubble reads cleanly.
			let displayMessage = "Generate a canvas report in the format: " + (selectedPromptDescription || format);
			// Hidden prompt body (was visible in the Instructions textarea pre-ESG-3925-followup). LLM only — never in the bubble.
			if (selectedPromptContent) {
				message += "\n\n" + selectedPromptContent;
			}
			// User's additional notes — visible in both the LLM payload and the chat bubble.
			if (additionalInstruction) {
				message += "\n\nAdditional instructions: " + additionalInstruction;
				displayMessage += "\n\nAdditional instructions: " + additionalInstruction;
			}
			// HACK extra nudge for TODOs (see ESG--, sep 2025)
			if (reportDoc.content?.includes("TODO")) {
				message += "\n\nFill in all TODOs in one canvas_edit. If data is not available, write a draft placeholder.";
			}
			const response = await postReportWriter({
				message,
				displayMessage,
				fileinfoSpec: _fileinfoSpec,
				agent: AGENT_CANVAS,
				reportdoc_id: reportDocId
			});
			// this just the Task info - not the actual report
			console.log("response", response);
			// reload the report doc to get the draft content
			queryClient.invalidateQueries(["reportDoc", reportDocId]);
		}
	}; // ./gotoNext

	// load the report doc
	// A reportDoc is always made and saved before we get to this page - by createReportDocAndGotoEditor()
	const { data: reportDoc, isLoading } = useQuery({
		queryKey: ["reportDoc", reportDocId],
		queryFn: async () => {
			await getAccessTokenSilently();
			return reportDocService.get(reportDocId);
		},
		onSuccess: (reportDoc) => {
			console.log("reportDoc loaded", reportDoc);
			// Advance from generating once the report is ready for editing.
			if (
				reportDoc.state === LifecycleState.DRAFT &&
				(isProcessing || STEPS[stepIndex] === "GENERATING")
			) {
				setIsProcessing(false);
				setStepIndex(EDIT_REPORT_STEP_INDEX);
			}
		},
		enabled: !!reportDocId,
		refetchInterval: isProcessing ? 5000 : false, // while the AI is processing, refetch every 5 seconds.
		// But don't refetch while the user is editing as that could overwrite and lose their edits
	});

	// HACK skip the first steps if we return to an in-progress wizard or an existing report
	// STEPS: 0=SELECT_SOURCES, 1=FORMAT, 2=GENERATING, 3=EDIT_REPORT
	const [is1stLoad, setIs1stLoad] = useState(true);
	useEffect(() => {
		if (!reportDoc) return; // loading
		if (stepIndex !== 0) return;
		if (!is1stLoad) return; // If the user did a couple of steps - then went back with (Previous) - don't skip them on again
		setIs1stLoad(false);
		// Have we done the 1st step (SELECT_SOURCES)?
		if (!reportDoc.sourcesAllowed?.length) return;
		let newStepIndex = 1; // FORMAT
		console.log("Returning to a report that is past step 0", reportDoc);
		// Have we done the 2nd step (FORMAT)? Pick-format will set the title
		if (reportDoc.title) {
			newStepIndex = 2; // GENERATING
		}
		// Have we done the 3rd step (GENERATING)?
		if (reportDoc.content || reportDoc.state === LifecycleState.DRAFT || reportDoc.state === LifecycleState.ACTIVE) {
			if (reportDoc.state === LifecycleState.PROCESSING) {
				console.log("AI processing not yet done - stay at generating");
				newStepIndex = 2;
			} else {
				newStepIndex = 3; // EDIT_REPORT
			}
		}
		setStepIndex(newStepIndex);
	}, [reportDoc, stepIndex, is1stLoad]);

	// set and save sources and questions
	// Local state for source selections — only saved to backend on Next click
	const [sourceTypedIds, setSourceTypedIds] = useState([]);
	const sourceInitRef = React.useRef(false);
	useEffect(() => {
		if (sourceInitRef.current || !reportDoc) return;
		if (reportDoc.sourcesAllowed?.length > 0) {
			sourceInitRef.current = true;
			setSourceTypedIds(reportDoc.sourcesAllowed);
		}
	}, [reportDoc]);


	const [format, _setFormat] = useState();
	const setFormat = (newFormat) => {
		_setFormat(newFormat);
		if (newFormat && !reportDoc.title) {
			reportDoc.title = toTitleCase(newFormat);
		}
	};
	if (step === "FORMAT") {
		nextDisabled = !format;
	} else if (step === "GENERATING") {
		nextDisabled = reportDoc.state === LifecycleState.PROCESSING;
	}

	const [additionalInstruction, setAdditionalInstruction] = useState();
	// Set when the user picks a Prompt Manager prompt — used as the user-visible chat bubble label.
	// Falls back to the format (== prompt.name) when no description is set.
	const [selectedPromptDescription, setSelectedPromptDescription] = useState();
	// The selected prompt's `content` (LLM-bound instruction). Held separately from
	// `additionalInstruction` so it never appears in the UI textarea — but still
	// gets folded into the message we send to the LLM in gotoNext().
	const [selectedPromptContent, setSelectedPromptContent] = useState();
	if (isLoading) {
		return (
			<div>
				<LocalSpinner />
			</div>
		);
	}

	if (step === "EDIT_REPORT") {
		return (
			<WideMarginsWrapper sidebarWidth={0} bg={colors.esg_gray[100]}>
				<ReportDocEditorPanel reportDocId={reportDoc.id} />
			</WideMarginsWrapper>
		);
	}

	return (
		<IssbContainer>
			{step === "GENERATING" && <GeneratingPanel reportDoc={reportDoc} setStepIndex={setStepIndex} />}
			{step !== "GENERATING" && (
				<WizardStepWrapper
					title={false}
					nextText={step === "FORMAT" ? "Generate" : "Next"}
					previousText="Previous"
					gotoNext={gotoNext}
					gotoPrevious={() => setStepIndex(stepIndex - 1)}
					stepIndex={stepIndex}
					steps={STEPS}
					nextDisabled={nextDisabled}
				>
					{step === "SELECT_SOURCES" && (
						<ReportDocSourcesPanel
							setNextDisabled={setNextDisabled}
							sourceTypedIds={sourceTypedIds}
							setSourceTypedIds={setSourceTypedIds}
							questionFilter={questionFilter}
							setQuestionFilter={setQuestionFilter}
						/>
					)}
					{step === "FORMAT" && (
						<FormatPanel
							format={format}
							setFormat={setFormat}
							additionalInstruction={additionalInstruction}
							setAdditionalInstruction={setAdditionalInstruction}
							setSelectedPromptDescription={setSelectedPromptDescription}
							setSelectedPromptContent={setSelectedPromptContent}
							reportDoc={reportDoc}
						/>
					)}
				</WizardStepWrapper>
			)}
		</IssbContainer>
	);
}

/**
 * Fallback when GET /prompts/filtered?location=report-doc-template returns [].
 * Other locations hide shortcut UI when empty (see ReportDocEditorPage consumers).
 */
export const REPORT_DOC_TEMPLATE_DEFAULT_PROMPT = {
	id: "__report-doc-template-default__",
	name: "Report",
	content:
		"Create a structured sustainability report from the selected sources. Include an executive summary, key disclosures, and sections grounded in the source material.",
};

function FormatPanel({ format, setFormat, additionalInstruction, setAdditionalInstruction, setSelectedPromptDescription, setSelectedPromptContent, reportDoc }) {
	const { prompts: fetchedPrompts, isLoading: isLoadingPrompts } = useContextualPrompts({ location: "report-doc-template" });
	// fallback if no prompts are returned
	let prompts = fetchedPrompts;
	if (!isLoadingPrompts && !fetchedPrompts?.length) {
		prompts = [REPORT_DOC_TEMPLATE_DEFAULT_PROMPT];
	}
	const [selectedPromptId, setSelectedPromptId] = useState("");

	const handlePromptSelect = (promptId) => {
		setSelectedPromptId(promptId);
		if (!promptId) {
			setFormat(null);
			setSelectedPromptDescription?.(undefined);
			setSelectedPromptContent?.(undefined);
			setAdditionalInstruction("");
			return;
		}
		const prompt = prompts.find(p => p.id === promptId);
		if (prompt) {
			// prompt.content drives the LLM but is hidden from the user — store on the parent.
			setSelectedPromptContent?.(prompt.content || undefined);
			// Fresh "Additional Instructions" box on each prompt switch — no stale notes leak across.
			setAdditionalInstruction("");
			setFormat(prompt.name);
			setSelectedPromptDescription?.(prompt.description || undefined);
		}
	};

	return (
		<>
			<PageHeader
				icon={<Sparkle weight="fill" size={32} color={colors.navIconLime} />}
				superTitle="Content Generator"
				title="Format"
			/>
			<PropInput item={reportDoc} prop="title" label={<H3>Title</H3>} placeholder="Give your report a title" />

			<HStack spacing={2} mt={4} mb={3}>
				<Sparkle size={16} weight="fill" color={colors.limeShade[600]} />
				<Text fontSize="body.s" fontWeight="semibold" color="limeShade.600">
					Shortcuts
				</Text>
			</HStack>
			<Flex gap={2} flexWrap="wrap" mb={4}>
				{isLoadingPrompts ? (
					<Text color="gray.400" fontSize="body.s">Loading prompts...</Text>
				) : (
					prompts.map((prompt) => (
						<Button
							key={prompt.id}
							variant="outline"
							size="sm"
							borderRadius="full"
							borderColor={selectedPromptId === prompt.id ? "headingLime" : "gray.300"}
							color={selectedPromptId === prompt.id ? "darkTeal" : "gray.600"}
							fontWeight={selectedPromptId === prompt.id ? "semibold" : "normal"}
							bg={selectedPromptId === prompt.id ? "limeTint.100" : "white"}
							_hover={{ borderColor: "headingLime", color: "darkTeal" }}
							onClick={() => handlePromptSelect(prompt.id)}
						>
							{prompt.description || prompt.name}
						</Button>
					))
				)}
			</Flex>

			<H3>Additional Instructions</H3>
			<Textarea
				value={additionalInstruction || ""}
				onChange={(e) => setAdditionalInstruction(e.target.value)}
				placeholder="Optional — add any extra notes for the AI on top of the selected template."
				rows={Math.max(4, Math.min((additionalInstruction || "").split("\n").length, 20))}
			/>
		</>
	);
}

function GeneratingPanel({ reportDoc, setStepIndex }) {
	// For routing purpose
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const toast = useToast();
	const { confirm } = useConfirmation();

	const isError = reportDoc.state === LifecycleState.ERROR;

	// For deleting the reportDoc object as a whole (It does not stop the backend process tho)
	const doDeleteDuringGeneratingStep = async () => {
		const confirmed = await confirm({
			title: "Delete Report " + reportDoc.title || "",
			message: "Are you sure you want to delete this report?",
			confirmText: "Delete",
			cancelText: "Cancel",
		});
		if (!confirmed) return;
		reportDocService.delete(reportDoc.id).then(() => {
			showSuccessToast(toast, "Report doc deleted", "report-doc-deleted");
			queryClient.invalidateQueries({ queryKey: ["reportDoc"] });
			navigate(routes.issbReportDoc);
		});
	};

	return (
		<>
			<PageHeader
				icon={<Sparkle weight="fill" size={32} color={colors.navIconLime} />}
				superTitle="Report Generator"
				title="Generating"
				subTitle={isError ? "There was an error" : "Stand by..."}
			/>
			<Box
				sx={{
					height: "auto",
					width: "auto",
					padding: 5,
					backgroundColor: `${colors.lightLime}`,
					borderRadius: "10px",
					border: `solid 1px ${colors.lime}`,
					// padding: 5,
					display: "flex",
					justifyContent: "flex-start",
					alignItems: "center",
					gap: 10,
				}}
			>
				{/* ESG-3952: real, live-updating progress (reads task.progress via GET /v0/task/{id})
				instead of the static "Stand by..." text this panel used to be limited to - the
				spinner above was previously commented out because progress had no real signal to
				show; AIProgressWidget (already used elsewhere for AI Review/reindex/source-analysis
				tasks) supplies that signal. reportDoc.taskId is set by both POST /v0/report-writer
				(initial generation) and the AI Insights chat's large-scope routing (ESG-3952). */}
				{!isError && reportDoc.taskId && (
					<Box height={"70px"} width={"50px"} display="flex" alignItems="center" justifyContent="center">
						<AIProgressWidget taskId={reportDoc.taskId} refetchInterval={5000} />
					</Box>
				)}

				<Box
					sx={{
						height: "100%",
						width: "100%",
						display: "flex",
						flexDirection: "column",
						justifyContent: "center",
						alignItems: "flex-start",
						gap: 5,
					}}
				>
					{!isError && <Text color={colors.lime} fontSize={"medium"} fontWeight={"bold"}>
						We're busy making your report. It will be available for editing in a few minutes.
					</Text>}
					{isError && <Text color={colors.esg_red} fontSize={"medium"} fontWeight={"bold"}>
						{"Sorry: There was an error generating the report."}
						{reportDoc.content && "Useful content is available in the unready draft report."}
					</Text>}

					{/* ESG-3952: the section checklist gives concrete progress (which sections the
					planner decided on, which are done/pending/failed) alongside AIProgressWidget's
					headline percentage on the left - complementary, not a replacement.
					ESG-3953: was gated on `!isError` - which hid the checklist in exactly the case
					it's most useful (a failed task is when the user most wants to know WHICH
					sections failed). Safe to show on error now that result.error is always a
					short, plain-language reason rather than raw exception text - the checklist
					degrades correctly by showing a red WarningDiamond per failed section. */}
					{reportDoc.taskId && (
						<Box width="100%" maxWidth="480px">
							<ReportSectionChecklist taskId={reportDoc.taskId} refetchInterval={5000} />
						</Box>
					)}

					<Box display={"flex"} width={"100%"} gap={3}>
						<Button
							sx={{
								backgroundColor: `${colors.lime}`,
								color: `${colors.pureBlack}`,
							}}
							onClick={() => {
								navigate(routes.issbReportDoc);
							}}
						>
							Ok, I'm done here
						</Button>
						{/* TODO a cancel button */}
						<Button
							onClick={() => {
								if (reportDoc) {
									doDeleteDuringGeneratingStep();
								}
							}}
						>
							Delete
						</Button>
						<DevOnly>
							<Button
								onClick={() => {
									setStepIndex(EDIT_REPORT_STEP_INDEX);
								}}
							>
								View Unready Report
							</Button>
						</DevOnly>
					</Box>
				</Box>
			</Box>
		</>
	);
}
